import React from 'react';
import {
  DataGrid,
  GridColDef,
  // GridToolbar,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { Box, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ErrorHandler from './Common/ErrorHandler';

interface CommonDataGridProps {
  rows: any[];
  columns: GridColDef[];
  pageSize: number;
  onPageSizeChange: (newSize: number) => void;
  loading?: boolean;
  error?: string;
  checkboxSelection?: boolean;
  selectionModel?: string[];
  onRowSelectionModelChange: (ids: string[]) => void;
  components: any;
  onPageChange?: (page: number) => void;
  rowCount?: number;
  page?: number;
  setQuery?: (params: Record<string, any>) => void;
}

const CommonDataGrid: React.FC<CommonDataGridProps> = ({
  rows,
  columns,
  pageSize,
  onPageSizeChange,
  loading = false,
  error = '',
  checkboxSelection = true,
  onRowSelectionModelChange,
  selectionModel,
  // components,
  onPageChange,
  rowCount = rows.length,
  page = 0,
  setQuery
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isRTL = theme.direction === 'rtl';
  const cellTextAlign = isRTL ? 'right' : 'left';
  const headerJustify = isRTL ? 'flex-end' : 'flex-start';
  // Handle row selection changes
  const handleRowSelectionChange = (
    newSelectionModel: GridRowSelectionModel
  ) => {
    const selectedIds = newSelectionModel as string[]; // Ensure the model is of type string[]
    onRowSelectionModelChange(selectedIds); // Pass selected IDs to parent
  };

  return (
      <Box sx={{ height: '75vh', width: '100%', position: 'relative' }}>
        {error && !loading && (
          <Box sx={{ mb: 2 }}>
            <ErrorHandler
              type={error === 'no-data' ? 'no-data' : 'network'}
              message={error === 'no-data' ? 'No data available.' : error}
              onRetry={() => { }}
            />
          </Box>
        )}

        <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            paginationModel={{ pageSize, page }}
            onPaginationModelChange={({ pageSize: newSize, page: newPage }) => {
              onPageSizeChange(newSize);
              onPageChange?.(newPage);
              setQuery?.({     
                limit: newSize,
                page: newPage + 1,
              });
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={handleRowSelectionChange}
            getRowId={(row: any) => row.id}
            rowCount={rowCount || rows.length}
            paginationMode="server"
            keepNonExistentRowsSelected
            initialState={{
              pagination: {
                paginationModel: { pageSize, page },
              },
            }}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: t('common.rowsPerPage'),
              },
              noRowsLabel: t('common.noResults'),
              noResultsOverlayLabel: t('common.noResults'),
            } as any}
            sx={{
              fontSize: 14,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f1f1f1',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: cellTextAlign,
              },
              '& .MuiDataGrid-columnHeader': {
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  justifyContent: headerJustify,
                },
              },
              '& .MuiDataGrid-cell': {
                textAlign: cellTextAlign,
              },
              '& .MuiTablePagination-root': {
                fontSize: 14,
              },
            }}
          />

          {loading && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 56,
                left: 0,
                right: 0,
                zIndex: 10,
              }}
            />
          )}
        </Box>
      </Box>
  );
};

export default CommonDataGrid;
